export function hasMatchingRequirement(course) {
    if (!course) return null;

    if (course.matching) {
        return course.matching;
    }

    const matchingPatterns = [
        /matching.*lecture.*tutorial/i,
        /matching.*lecture.*lab/i,
        /L[0-9]+-T[0-9]+/,
        /Lec.*Tut.*match/i,
    ];

    if (course.sections && Array.isArray(course.sections)) {
        for (const section of course.sections) {
            if (section.remarks && Array.isArray(section.remarks)) {
                for (const remark of section.remarks) {
                    for (const pattern of matchingPatterns) {
                        if (pattern.test(remark)) {
                            return remark;
                        }
                    }
                }
            }
        }
    }

    return null;
}

export function parseMatchingPairs(matchingStr) {
    if (!matchingStr) return [];

    const pairs = [];

    const pairMatches = matchingStr.match(/\b(L\d+)\s*-\s*(T\d+[A-Z]*|LA\d+[A-Z]*)\b/gi);

    if (pairMatches) {
        for (const match of pairMatches) {
            const parts = match.split('-').map(part => part.trim());
            if (parts.length === 2) {
                pairs.push({
                    lecture: parts[0].toUpperCase(),
                    tutorial: parts[1].toUpperCase(),
                });
            }
        }
    }

    return pairs;
}

function normalizeSectionId(section) {
    const match = String(section || '').toUpperCase().match(/^([A-Z]+)0*(\d+)([A-Z]*)/);
    return match ? `${match[1]}${Number(match[2])}${match[3]}` : String(section || '').toUpperCase();
}

function getSectionType(section) {
    return normalizeSectionId(section).match(/^[A-Z]+/)?.[0] || '';
}

function getSectionNumber(section) {
    return normalizeSectionId(section).match(/^[A-Z]+(\d+)/)?.[1] || null;
}

export function sectionMatchesPrefix(section, prefix) {
    const normalizedSection = normalizeSectionId(section);
    const normalizedPrefix = normalizeSectionId(prefix);

    const sectionParts = normalizedSection.match(/^([A-Z]+)(\d+)(.*$)/);
    const prefixParts = normalizedPrefix.match(/^([A-Z]+)(\d+)(.*$)/);
    if (!sectionParts || !prefixParts) return normalizedSection === normalizedPrefix;

    return sectionParts[1] === prefixParts[1]
        && Number(sectionParts[2]) === Number(prefixParts[2])
        && sectionParts[3].startsWith(prefixParts[3]);
}

export function getMatchingPairs(course) {
    const matchingStr = hasMatchingRequirement(course);
    const explicitPairs = parseMatchingPairs(matchingStr);
    if (explicitPairs.length > 0) return explicitPairs;
    if (!matchingStr || !Array.isArray(course?.sections)) return [];

    const matchingText = String(matchingStr).toLowerCase();
    const dependentTypes = matchingText.includes('lab') && !matchingText.includes('tutorial')
        ? ['LA']
        : matchingText.includes('tutorial') && !matchingText.includes('lab')
            ? ['T']
            : ['T', 'LA'];
    const lectures = course.sections
        .map(item => item.section)
        .filter(section => getSectionType(section) === 'L');
    const dependents = course.sections
        .map(item => item.section)
        .filter(section => dependentTypes.includes(getSectionType(section)));

    return lectures.flatMap(lecture => dependents
        .filter(section => getSectionNumber(section) === getSectionNumber(lecture))
        .map(section => ({
            lecture: lecture.toUpperCase(),
            tutorial: section.toUpperCase(),
        })));
}

export function getMatchingTutorials(course, lecture, availableTutorials = []) {
    const selectedLecture = normalizeSectionId(lecture);
    if (!selectedLecture) return [];

    const tutorialPrefixes = getMatchingPairs(course)
        .filter(pair => normalizeSectionId(pair.lecture) === selectedLecture)
        .map(pair => pair.tutorial);

    return availableTutorials.filter(section => (
        tutorialPrefixes.some(prefix => sectionMatchesPrefix(section, prefix))
    ));
}

export function validateMatching(course, selectedSections) {
    const matchingStr = hasMatchingRequirement(course);

    if (!matchingStr) {
        return { isValid: true, message: null };
    }

    const pairs = getMatchingPairs(course);

    if (pairs.length === 0) {
        return {
            isValid: true,
            message: 'Note: Matching between Lecture & Tutorial required'
        };
    }

    const selectedLecture = selectedSections.find(s => /^L\d/i.test(s))?.toUpperCase();
    const dependentTypes = new Set(pairs.map(pair => getSectionType(pair.tutorial)));
    const selectedTutorial = selectedSections
        .find(section => dependentTypes.has(getSectionType(section)))
        ?.toUpperCase();

    if (!selectedLecture || !selectedTutorial) {
        return { isValid: true, message: null };
    }

    const matchingPair = pairs.find(
        p => sectionMatchesPrefix(selectedLecture, p.lecture)
            && sectionMatchesPrefix(selectedTutorial, p.tutorial)
    );

    if (matchingPair) {
        return { isValid: true, message: null };
    }

    const expectedForLecture = pairs.find(p => sectionMatchesPrefix(selectedLecture, p.lecture));
    const expectedForTutorial = pairs.find(p => sectionMatchesPrefix(selectedTutorial, p.tutorial));

    let suggestion = '';
    if (expectedForLecture) {
        suggestion = `${selectedLecture} should pair with ${expectedForLecture.tutorial}`;
    } else if (expectedForTutorial) {
        suggestion = `${selectedTutorial} should pair with ${expectedForTutorial.lecture}`;
    }

    return {
        isValid: false,
        message: `⚠️ Matching violation! ${suggestion}`
    };
}

export function enforceMatchingSelections(course, selectedSections, preferredSection) {
    const pairs = getMatchingPairs(course);
    const uniqueSections = [...new Set(selectedSections)];
    if (pairs.length === 0) return uniqueSections;

    const preferred = preferredSection?.toUpperCase();
    const availableSections = course?.sections?.map(item => item.section).filter(Boolean) || [];

    let pair;
    if (preferred && /^L\d/i.test(preferred)) {
        pair = pairs.find(item => sectionMatchesPrefix(preferred, item.lecture));
    } else if (preferred && /^(T|LA)\d/i.test(preferred)) {
        pair = pairs.find(item => sectionMatchesPrefix(preferred, item.tutorial));
    } else {
        const currentLecture = uniqueSections.find(s => /^L\d/i.test(s))?.toUpperCase();
        pair = currentLecture && pairs.find(item => sectionMatchesPrefix(currentLecture, item.lecture));
    }
    if (!pair && pairs.length > 0) pair = pairs[0];
    if (!pair) return uniqueSections;

    const resolveSection = (prefix, preferredMatch) => (
        availableSections.find(section => section === prefix)
        || (preferredMatch && sectionMatchesPrefix(preferredMatch, prefix)
            ? availableSections.find(section => section === preferredMatch)
            : null)
        || availableSections.find(section => sectionMatchesPrefix(section, prefix))
        || null
    );

    const lecture = resolveSection(pair.lecture,
        preferred && /^L\d/i.test(preferred) ? preferred : null);
    const tutorial = resolveSection(pair.tutorial,
        preferred && /^(T|LA)\d/i.test(preferred) ? preferred : null);

    if (!lecture || !tutorial) return uniqueSections;

    const dependentTypes = new Set(pairs.map(item => getSectionType(item.tutorial)));
    return [
        ...uniqueSections.filter(section => (
            !/^L\d/i.test(section) && !dependentTypes.has(getSectionType(section))
        )),
        lecture,
        tutorial,
    ];
}

export function getMatchingExclusion(course, lecture, sections = {}) {
    const pairs = getMatchingPairs(course);
    const matchingTypes = [...new Set(
        pairs
            .filter(pair => sectionMatchesPrefix(lecture, pair.lecture))
            .map(pair => getSectionType(pair.tutorial))
    )];
    const matchingSections = matchingTypes.flatMap(type => (
        getMatchingTutorials(course, lecture, sections[type] || [])
    ));
    const hiddenSectionCount = matchingTypes.reduce((count, type) => (
        count + Math.max(0, (sections[type]?.length || 0)
            - getMatchingTutorials(course, lecture, sections[type] || []).length)
    ), 0);

    return matchingSections.length > 0 && hiddenSectionCount > 0
        ? { lecture, sections: matchingSections, hiddenSectionCount }
        : null;
}
